import PropTypes from 'prop-types';
import React from 'react';

import logger from '@/shared/utils/logger';

import { normalizeMapDocument } from '../utils/mapDocument';

import BiliMap from './BiliMap';
import EmbeddedMapFrame from './EmbeddedMapFrame';
import MapErrorBoundary from './MapErrorBoundary';
import MapUnavailable from './MapUnavailable';

/** A string field from the raw payload, or '' if it is anything else. */
const stringOr = (value) => (typeof value === 'string' ? value : '');

/**
 * The note shown under the map for a layer that was skipped, so the reader
 * knows why the map has less on it than its caption describes. A layer
 * skipped for having no label still gets a note, just without its name.
 */
function skippedLayerNote(label) {
    return label
        ? `${label} could not be drawn.`
        : 'One map layer could not be drawn.';
}

/**
 * The map itself, from an already-present payload: check it against the map
 * document v3 contract (normalizeMapDocument, which also adapts TRANSITIONAL(v1)
 * payloads), log every skip, then draw it.
 *
 * Wrapped in a <figure> whose <figcaption> is the document caption, linked
 * with aria-labelledby, so the caption names the map for screen readers. A
 * figure may hold interactive content, unlike the role="img" wrapper this
 * replaces (#1190).
 */
function MapDocumentView({ payload }) {
    const captionId = React.useId();
    // Memoized so BiliMap gets a stable `layers` array: it rebuilds every
    // overlay when that identity changes, and recreating a 1,000-polygon
    // choropleth on each parent re-render is expensive.
    const mapDocument = React.useMemo(
        () => normalizeMapDocument(payload),
        [payload]
    );

    React.useEffect(() => {
        mapDocument.warnings.forEach((warning) => {
            if (warning.scope === 'document') {
                logger.warn(
                    {
                        render_type: warning.render_type,
                        version: warning.version,
                        reason: warning.reason,
                    },
                    'Map payload with no matching renderer'
                );
                return;
            }
            logger.warn(
                {
                    layer_id: warning.layer_id,
                    label: warning.label,
                    reason: warning.reason,
                },
                'Skipped map content that does not match the v3 contract'
            );
        });
    }, [mapDocument]);

    const { mapLayers, embedLayers, skippedLayers, title, caption } =
        mapDocument;
    if (mapLayers.length === 0 && embedLayers.length === 0) {
        // The engine sent a map, but every layer was skipped (or the document
        // was rejected outright). Say so, the same way a render error does,
        // instead of leaving Bili's "here is a map" with nothing under it.
        // A rejected document has no normalized title/caption, so fall back
        // to the raw payload's.
        return (
            <MapUnavailable
                title={title || stringOr(payload.title)}
                caption={caption || stringOr(payload.caption)}
            />
        );
    }

    return (
        <figure
            className="map-payload mb-0"
            // The caption names the figure. aria-labelledby makes that explicit,
            // since not every screen reader derives a figure's name from its
            // <figcaption>. With no caption (e.g. v1 payloads), the title does.
            aria-labelledby={caption ? captionId : undefined}
            aria-label={caption ? undefined : title || undefined}
        >
            {mapLayers.length > 0 && (
                <BiliMap layers={mapLayers} title={title} />
            )}
            {embedLayers.map((layer) => (
                <React.Fragment key={layer.id}>
                    <EmbeddedMapFrame
                        className={mapLayers.length > 0 ? 'mt-2' : ''}
                        embedCode={layer.embed_code}
                        fallbackUrl={layer.fallback_url}
                        title={layer.label}
                        embeddable={layer.embeddable}
                    />
                    {layer.attribution && (
                        <p className="text-muted small mt-1 mb-0">
                            Source: {layer.attribution}
                        </p>
                    )}
                </React.Fragment>
            ))}
            {/* The caption still describes every layer the engine sent; these
                notes say which ones are missing (the reason is in the logged
                warning, not shown: it is for developers, not readers). */}
            {skippedLayers.map((skipped, index) => (
                <p
                    // Index, not layer_id: a skipped layer may have no id, or
                    // a duplicate one. The list never reorders.
                    key={index}
                    className="text-muted small mt-2 mb-0"
                >
                    {skippedLayerNote(skipped.label)}
                </p>
            ))}
            {caption && (
                <figcaption
                    id={captionId}
                    className="text-muted small mt-2 mb-0"
                >
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}

MapDocumentView.propTypes = {
    payload: PropTypes.object.isRequired,
};

/**
 * Render the `map` object from an engine chat response.
 *
 * The engine sends a v3 map document (engine docs/map-document-v3-contract.md):
 * several layers, each with a resolved style, legend, and popup spec, plus a
 * caption. MapDocumentView checks it, logs anything skipped (naming the layer
 * and the reason), and draws it.
 *
 * Everything is inside MapErrorBoundary, including the contract check: if
 * anything throws while rendering this map, the reader sees the "Map
 * unavailable" card plus the caption (MapUnavailable) in its place, and the
 * rest of the chat is untouched. The fallback reads title/caption straight
 * from the raw payload, because the check itself may be what failed.
 *
 * Renders nothing for an absent payload (most turns have no map). A payload
 * with nothing drawable in it gets the same "Map unavailable" card plus
 * caption, with the reasons in the logged warnings.
 */
export default function MapPayload({ payload }) {
    if (payload === null || payload === undefined) return null;
    return (
        <MapErrorBoundary
            resetKey={payload}
            context={{ map_id: stringOr(payload.map_id) }}
            fallback={
                <MapUnavailable
                    title={stringOr(payload.title)}
                    caption={stringOr(payload.caption)}
                />
            }
        >
            <MapDocumentView payload={payload} />
        </MapErrorBoundary>
    );
}

MapPayload.propTypes = {
    /**
     * The `map` object from an engine chat response: a v3 map document, a v1
     * single-dataset payload (TRANSITIONAL(v1)), or absent. Anything else is
     * rejected by normalizeMapDocument() with a logged warning, not a crash.
     */
    payload: PropTypes.object,
};
